import { useTranslation } from "react-i18next";
import { ShieldAlert, ShieldX, Info } from "lucide-react";
import { formatMoney } from "../../lib/currency";
import { priceGate, priceRange } from "../../lib/priceGate";

/**
 * Banner that explains WHY the price is not firm. Rendered above the tabs on
 * the quote detail page and at the top of the Costs tab.
 */
export function PriceGateBanner({ guardrail, total, currency }) {
  const { t } = useTranslation();
  const gate = priceGate(guardrail);
  if (gate.exact) return null;
  const Icon = gate.blocked ? ShieldX : ShieldAlert;
  const range = priceRange(total, gate.band);

  return (
    <div
      className={`mb-6 rounded-md border px-4 py-3 flex items-start gap-3 ${gate.styles.border} bg-slate-900/60`}
      data-testid="price-gate-banner"
      data-gate-mode={gate.mode}
    >
      <Icon size={16} className={`${gate.styles.text} mt-0.5 shrink-0`} />
      <div className="flex-1 min-w-0">
        <div className={`label-tech text-[10px] ${gate.styles.text} mb-1`}>
          {t(`priceGate.${gate.mode}Label`)}
          {gate.score !== null ? ` · ${gate.score}/100` : ""}
        </div>
        <p className="text-sm text-slate-300 leading-relaxed">{t(`priceGate.${gate.mode}Body`)}</p>
        {!gate.blocked && (
          <div className="mt-2 flex items-baseline gap-2 flex-wrap">
            <span className="text-[10px] label-tech text-slate-500">{t("priceGate.rangeLabel")}</span>
            <span className={`font-mono-data ${gate.styles.size} ${gate.styles.text}`} data-testid="price-gate-range">
              {formatMoney(range.low, currency)} – {formatMoney(range.high, currency)}
            </span>
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded border font-mono-data ${gate.styles.chip}`}
              data-testid="price-gate-band"
            >
              {gate.band.low_pct}% / +{gate.band.high_pct}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The total-price metric, rendered with a visual weight proportional to how
 * much the estimate can be trusted.
 */
export function GatedPriceMetric({ guardrail, total, currency, icon, label, testid }) {
  const { t } = useTranslation();
  const gate = priceGate(guardrail);
  const range = priceRange(total, gate.band);

  return (
    <div className={`tech-card p-4 ${gate.styles.border}`} data-testid={testid} data-gate-mode={gate.mode}>
      <div className="flex items-center gap-2 text-slate-400 mb-2">
        {icon}
        <div className="label-tech">{label}</div>
      </div>
      {gate.blocked ? (
        <>
          <div className={`font-mono-data ${gate.styles.size} ${gate.styles.text}`} data-testid="metric-total-blocked">—</div>
          <div className="text-[10px] text-rose-300/80 mt-1 leading-tight">{t("priceGate.blockedLabel")}</div>
        </>
      ) : gate.exact ? (
        <div className="font-mono-data text-2xl text-blue-400">{formatMoney(total, currency)}</div>
      ) : (
        <>
          <div className={`font-mono-data ${gate.styles.size} ${gate.styles.text} leading-tight`} data-testid="metric-total-range">
            {formatMoney(range.low, currency)} – {formatMoney(range.high, currency)}
          </div>
          <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-1 leading-tight">
            <Info size={10} /> {t(`priceGate.${gate.mode}Label`)}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Replaces the Costs tab content when the extraction is too weak to price.
 */
export function CostsLockedCard({ guardrail }) {
  const { t } = useTranslation();
  const gate = priceGate(guardrail);
  return (
    <div className="tech-card p-8 text-center" data-testid="costs-locked">
      <div className="w-14 h-14 mx-auto rounded-md border border-rose-500/40 bg-rose-500/10 flex items-center justify-center mb-4">
        <ShieldX size={24} className="text-rose-300" />
      </div>
      <h3 className="font-display font-bold text-lg text-slate-100 mb-2">{t("priceGate.blockedLabel")}</h3>
      <p className="text-sm text-slate-400 max-w-md mx-auto">{t("priceGate.costsLockedBody")}</p>
      {gate.score !== null && (
        <div className="mt-4 inline-block text-[11px] font-mono-data px-2 py-1 rounded border border-rose-500/40 bg-rose-500/10 text-rose-300">
          GUARDRAIL {gate.score}/100
        </div>
      )}
    </div>
  );
}
